import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import LoadErrorBox from './ui/LoadErrorBox';
import ComposeModal from './communications/ComposeModal';
import RsvpReminderModal from './communications/RsvpReminderModal';
import ConversationPane from './communications/ConversationPane';
import ProviderNotice from './communications/ProviderNotice';
import ThreadList from './communications/ThreadList';
import { useCommunicationsData } from './communications/useCommunicationsData';
import { requestRefresh, useInboxUi } from './communications/inboxUiStore';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { useNotifications } from './NotificationProvider';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useRole } from '../hooks/useRole';
import {
  CommunicationsError,
  deleteMessage,
  mapCommunicationsError,
  markMessageRead,
  markThreadRead,
  muteThread,
  sendMessage,
  unmuteThread,
} from '../services/CommunicationsService';
import { buildThreads, filterThreads, inboxMetrics, replyTarget, type InboxThread } from '../utils/inboxThreads';
import type { InboxMessage, SendMessageInput } from '../types/Communications';
import '../styles/components/CommunicationsView.css';

/**
 * Communications: the project's inbox as a two-pane screen (conversations on
 * the left, the open conversation on the right). Conversations are grouped by
 * the server's thread_key. The filter, Compose and Refresh live in the
 * breadcrumb bar (see communications/CommunicationsBar) and reach this view
 * through the shared inboxUiStore.
 */
const CommunicationsView: React.FC = () => {
  const { canEdit, isAdmin } = useRole();
  const navigate = useNavigate();
  const { activeProjectId } = useProject();
  const { showError } = useNotifications();
  const ui = useInboxUi();
  const { messages, mutedThreads, provider, loading, loadError, refresh, reloadProvider } =
    useCommunicationsData(activeProjectId);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InboxMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const threads = useMemo(() => buildThreads(messages, mutedThreads), [messages, mutedThreads]);
  const visible = useMemo(() => filterThreads(threads, ui.filter), [threads, ui.filter]);
  const metrics = useMemo(() => inboxMetrics(messages), [messages]);
  const selected = useMemo(() => threads.find(t => t.key === selectedKey) ?? null, [threads, selectedKey]);

  // Sending is off only when the server says WhatsApp is not set up. While the
  // status is unknown the server still checks (409), and that is shown too.
  const sendBlocked = provider !== null && !provider.canSend;

  useEffect(() => { setSelectedKey(null); }, [activeProjectId]);

  // One-shot requests from the breadcrumb bar. The nonces seen at mount are
  // ignored so an old click does not replay when the screen opens.
  const seen = useRef({ compose: ui.composeNonce, refresh: ui.refreshNonce, reminder: ui.reminderNonce });
  useEffect(() => {
    if (ui.composeNonce !== seen.current.compose) {
      seen.current.compose = ui.composeNonce;
      if (canEdit) setComposeOpen(true);
    }
    if (ui.refreshNonce !== seen.current.refresh) {
      seen.current.refresh = ui.refreshNonce;
      void refresh();
      void reloadProvider();
    }
    if (ui.reminderNonce !== seen.current.reminder) {
      seen.current.reminder = ui.reminderNonce;
      if (canEdit) setReminderOpen(true);
    }
  }, [ui.composeNonce, ui.refreshNonce, ui.reminderNonce, canEdit, refresh, reloadProvider]);

  // The header's Refresh asks this view through the store.
  usePageRefresh(requestRefresh);

  const markRead = useCallback(async (thread: InboxThread) => {
    try {
      if (thread.threadKey) await markThreadRead(thread.threadKey);
      else await Promise.all(thread.items.filter(m => !m.read).map(m => markMessageRead(m.id)));
    } catch (err) {
      showError(mapCommunicationsError(err).message);
    }
  }, [showError]);

  const select = (thread: InboxThread) => {
    setSelectedKey(thread.key);
    // Viewers only read: opening a conversation does not change it for them.
    if (canEdit && thread.unread > 0) void markRead(thread);
  };

  const doSend = useCallback(async (input: SendMessageInput, key: string): Promise<InboxMessage> => {
    try {
      return await sendMessage(input, key);
    } catch (err) {
      const mapped = mapCommunicationsError(err);
      // The setup changed under us: re-read it so the buttons and notice follow.
      if (mapped instanceof CommunicationsError && mapped.isProviderNotConfigured) void reloadProvider();
      throw mapped;
    }
  }, [reloadProvider]);

  const reply = async (thread: InboxThread, text: string, key: string) => {
    const target = replyTarget(thread);
    if (!target) throw new Error('This conversation cannot be replied to.');
    await doSend({ text, ...target }, key);
  };

  const toggleMute = async (thread: InboxThread) => {
    if (!thread.threadKey) return;
    try {
      await (thread.muted ? unmuteThread(thread.threadKey) : muteThread(thread.threadKey));
    } catch (err) {
      showError(mapCommunicationsError(err).message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteMessage(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(mapCommunicationsError(err).message);
    } finally {
      setDeleting(false);
    }
  };

  const onComposed = (message: InboxMessage) => {
    setComposeOpen(false);
    setSelectedKey(message.thread_key ?? `msg:${message.id}`);
  };

  const hasMessages = threads.length > 0;

  return (
    <div className="wt-container fade-in communications-view">
      {sendBlocked && <ProviderNotice className="communications-provider-banner" />}

      <section className="stats-grid" aria-label="Message summary">
        <MetricCard label="Messages" value={metrics.total} tone="blue" icon="inbox" />
        <MetricCard label="Unread" value={metrics.unread} tone="orange" icon="message" />
        <MetricCard label="Sent" value={metrics.sent} tone="green" icon="send" />
        <MetricCard label="Not delivered" value={metrics.failed} tone="red" icon="alert" />
      </section>

      {loadError != null && hasMessages && (
        <div className="communications-error communications-error--bar" role="alert">
          <span>Couldn't refresh messages. Showing the last ones loaded.</span>
          <Button variant="secondary" size="sm" onClick={() => void refresh()}>Retry</Button>
        </div>
      )}

      {loadError != null && !hasMessages ? (
        <LoadErrorBox what="messages" error={loadError} onRetry={() => void refresh()} retrying={loading} />
      ) : loading && !hasMessages ? (
        <p className="communications-loading" role="status">Loading messages…</p>
      ) : !hasMessages ? (
        <EmptyState
          icon="inbox"
          title="No messages yet"
          description="Messages you send and replies from guests will appear here."
          action={canEdit ? (
            <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={() => setComposeOpen(true)}>New message</Button>
          ) : undefined}
        />
      ) : (
        <div className={`communications-panes${selected ? ' communications-panes--has-selection' : ''}`}>
          <div className="communications-list-pane">
            {visible.length === 0 ? (
              <EmptyState compact icon="inbox" title="No conversations match this filter" description="Choose another option in Filters." />
            ) : (
              <ThreadList threads={visible} selectedKey={selectedKey} onSelect={select} />
            )}
          </div>
          <div className="communications-detail-pane">
            {selected ? (
              <ConversationPane
                key={selected.key}
                thread={selected}
                canEdit={canEdit}
                sendBlocked={sendBlocked}
                onSend={reply}
                onToggleMute={toggleMute}
                onDelete={setDeleteTarget}
                onBack={() => setSelectedKey(null)}
              />
            ) : (
              <div className="communications-detail-empty">
                <Icon name="message-circle" size={32} />
                <p>Select a conversation</p>
                <span>Pick one on the left to read the full thread.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <section className="communications-provider-panel" aria-label="WhatsApp setup">
          <p className="communications-hint">
            {provider?.configured ? 'WhatsApp is connected.' : 'WhatsApp is not connected.'} Its setup is in Workspace settings.
          </p>
          <div className="communications-provider-actions">
            <Button variant="secondary" onClick={() => navigate('/workspace/settings#whatsapp')}>Open WhatsApp setup</Button>
          </div>
        </section>
      )}

      {canEdit && (
        <ComposeModal
          open={composeOpen}
          onClose={() => setComposeOpen(false)}
          sendBlocked={sendBlocked}
          onSend={doSend}
          onDone={onComposed}
        />
      )}

      {canEdit && (
        <RsvpReminderModal
          open={reminderOpen}
          onClose={() => setReminderOpen(false)}
          onSent={() => void refresh()}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => { setDeleteTarget(null); setDeleteError(null); }}
        onConfirm={confirmDelete}
        title="Delete this message?"
        description="It is removed from this project's inbox. This cannot be undone."
        error={deleteError}
        loading={deleting}
      />
    </div>
  );
};

export default CommunicationsView;
